import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Checkbox, HStack, VStack, useToast } from "@chakra-ui/react";

import { extractErrorMessage } from "@equidefi/portals";
import { MfaCodeInput } from "@equidefi/portals/components/MfaCodeInput";
import {
  useRegistrationContext,
  useRegistrationLogin,
  useRegistrationMfa,
  useRegistrationSetup,
  useRegistrationVerify,
} from "@equidefi/portals/hooks/useAuthentication";
import { useCountdown } from "@equidefi/portals/hooks/useCountdown";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { usePasswordStrength } from "@equidefi/portals/hooks/usePasswordStrength";
import { Heading, Password, Text } from "@equidefi/ui";

import { PoweredBySection } from "@/components/nav/components";
import { OfferingInfoCard } from "@/components/workflow/OfferingInfoCard";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";

import { Link } from "react-router";
import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import { useWorkflowContext } from "../context/WorkflowContext";

const INVALID_LINK_MESSAGE = "This registration link is invalid or has expired";

type Step = "loading" | "login" | "code" | "password";

const OpenMfaContainer = ({ offering, children }: { offering: any; children: React.ReactNode }) => (
  <WorkflowContainer title={`Welcome to ${offering.issuer.name}'s offering!`}>
    <OfferingInfoCard offering={offering} />
    {children}
    <PoweredBySection variant="light" />
  </WorkflowContainer>
);

const WorkflowOpenMfa = () => {
  const { slug, token } = useParams();
  const { offering } = useWorkflowContext();
  const navigate = useNavigate();
  const toast = useToast();

  const context = useRegistrationContext(token as string);
  const mfa = useRegistrationMfa(token as string);
  const login = useRegistrationLogin(token as string);
  const verify = useRegistrationVerify(token as string);
  const setup = useRegistrationSetup(token as string);
  const { countdown, isActive, startCountdown } = useCountdown();
  const { score, analyze, validate } = usePasswordStrength();

  const [step, setStep] = useState<Step>("loading");
  const [mode, setMode] = useState<"mfa" | "login">("mfa");
  const [mfaCode, setMfaCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [destination, setDestination] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  // StrictMode double-invokes effects; one context fetch per page load is enough.
  const initialized = useRef(false);

  useDocumentTitle("Verify Your Identity");

  const sendCode = async () => {
    const res = await mfa.mutateAsync();
    setDestination(res.destination);
    startCountdown();
    return res;
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    context
      .mutateAsync()
      .then(async (res) => {
        setMode(res.mode);
        setDestination(res.destination);

        if (res.mode === "login") {
          setStep("login");
          return;
        }

        // New investor: the code email goes out as soon as the page loads.
        const sent = await mfa.mutateAsync();
        setDestination(sent.destination);
        startCountdown();
        setStep("code");
      })
      .catch((error) => {
        setLinkError(extractErrorMessage(error, INVALID_LINK_MESSAGE));
      });
  }, [context, mfa, startCountdown]);

  const onLogin = async (event: { preventDefault: () => void }) => {
    event.preventDefault();

    try {
      const res = await login.mutateAsync(password);
      setDestination(res.destination);
      setPassword("");
      startCountdown();
      setStep("code");
    } catch (error) {
      toast({
        status: "error",
        description: extractErrorMessage(error, "There was an error trying to log in."),
      });
    }
  };

  const onResend = async () => {
    if (isActive || mfa.isLoading) return;

    try {
      const res = await sendCode();
      toast({ status: "info", description: `New code sent to ${res.destination}` });
    } catch (error) {
      toast({ status: "error", description: extractErrorMessage(error, INVALID_LINK_MESSAGE) });
    }
  };

  const goToInvestment = (investment: { id: string; next?: string | null }) => {
    // Materialization guarantees at least SUBSCRIPTION; the fallback keeps a
    // null `next` from building a /undefined route.
    const next = investment.next?.toLowerCase() ?? "subscription";
    navigate(`/offerings/${slug}/${investment.id}/${next}`);
  };

  const onVerify = async (event: { preventDefault: () => void }) => {
    event.preventDefault();

    try {
      const res = await verify.mutateAsync(mfaCode);
      if (res.investment) {
        goToInvestment(res.investment);
        return;
      }

      // New investor: their account exists now, but the investment waits for
      // the password + Terms of Service step.
      setStep("password");
    } catch (error) {
      toast({
        status: "error",
        description: extractErrorMessage(error, "Incorrect code, please try again"),
      });
    }
  };

  const onSetup = async (event: { preventDefault: () => void }) => {
    event.preventDefault();

    try {
      const res = await setup.mutateAsync({ password, password_confirmation: passwordConfirmation });
      goToInvestment(res.investment);
    } catch (error) {
      toast({
        status: "error",
        description: extractErrorMessage(error, "There was an error setting your password."),
      });
    }
  };

  const passwordStrengthError = password.length > 0 ? validate(password) : null;
  const passwordsMatch = password.length > 0 && password === passwordConfirmation;
  const setupReady = passwordStrengthError === true && passwordsMatch;

  if (linkError) {
    return (
      <OpenMfaContainer offering={offering}>
        <Heading textStyle="h2" fontSize="xl" fontWeight="700" m={0}>
          Something went wrong
        </Heading>
        <Text textStyle="body2">
          {linkError}. Please contact the party that referred you to restart your registration.
        </Text>
      </OpenMfaContainer>
    );
  }

  if (step === "loading") {
    return (
      <OpenMfaContainer offering={offering}>
        <Heading textStyle="h2" fontSize="xl" fontWeight="700" m={0}>
          Verify Your Identity
        </Heading>
        <Text textStyle="body2">Preparing your registration…</Text>
      </OpenMfaContainer>
    );
  }

  if (step === "login") {
    return (
      <OpenMfaContainer offering={offering}>
        <Heading textStyle="h2" fontSize="xl" fontWeight="700" m={0}>
          Welcome Back
        </Heading>
        <Text textStyle="body2">
          You already have an EquiDeFi account for {destination}. Log in to continue your investment.
        </Text>
        <WorkflowForm onSubmit={onLogin}>
          <Password
            name="password"
            label="Password"
            isRequired
            borderRadius={5}
            value={password}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
          />
          <WorkflowButton isDisabled={password.length === 0} isLoading={login.isLoading} onClick={onLogin} mb={5}>
            Log In
          </WorkflowButton>
        </WorkflowForm>
      </OpenMfaContainer>
    );
  }

  if (step === "password") {
    return (
      <OpenMfaContainer offering={offering}>
        <Heading textStyle="h2" fontSize="xl" fontWeight="700" my={4}>
          Set Your Password
        </Heading>
        <WorkflowForm onSubmit={onSetup}>
          <Password
            name="password"
            label="Enter a Password"
            isRequired
            borderRadius={5}
            strength={score}
            error={passwordStrengthError !== true ? (passwordStrengthError ?? undefined) : undefined}
            isInvalid={password.length > 0 && passwordStrengthError !== true}
            showPasswordOverwrite={showPassword}
            value={password}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              analyze(event.target.value);
              setPassword(event.target.value);
            }}
          />
          <Password
            name="password_confirmation"
            label="Confirm Password"
            isRequired
            borderRadius={5}
            showPasswordOverwrite={showPassword}
            error={passwordConfirmation.length > 0 && !passwordsMatch ? "Passwords do not match" : undefined}
            isInvalid={passwordConfirmation.length > 0 && !passwordsMatch}
            value={passwordConfirmation}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPasswordConfirmation(event.target.value)}
          />
          <Checkbox mb={4} size="md" isChecked={showPassword} onChange={() => setShowPassword((prev) => !prev)}>
            Show passwords
          </Checkbox>
          <Text textStyle="context" mb={4}>
            By creating an account you are agreeing to the{" "}
            <Text
              textStyle="context"
              as={Link}
              _hover={{ color: "blue.700" }}
              color="blue.600"
              to="https://equidefi.com/terms-of-service/"
              target="_blank">
              Terms of Service
            </Text>{" "}
            and{" "}
            <Text
              textStyle="context"
              as={Link}
              _hover={{ color: "blue.700" }}
              color="blue.600"
              to="https://equidefi.com/privacy-policy"
              target="_blank">
              Privacy Policy
            </Text>{" "}
            and consenting to receive email or SMS messages regarding your investment and other relevant offers.
          </Text>
          <WorkflowButton
            isDisabled={!setupReady}
            isLoading={setup.isLoading}
            loadingText="Setting up your investment…"
            onClick={onSetup}
            mb={5}>
            Create Your Account
          </WorkflowButton>
        </WorkflowForm>
      </OpenMfaContainer>
    );
  }

  return (
    <OpenMfaContainer offering={offering}>
      <Heading textStyle="h2" fontSize="xl" fontWeight="700" m={0}>
        Verify Your Identity
      </Heading>
      <Text textStyle="body2">
        {destination
          ? `To continue your investment, enter the 6-digit code we sent to ${destination}.`
          : "Sending your verification code…"}
      </Text>
      <WorkflowForm onSubmit={onVerify}>
        <VStack>
          <MfaCodeInput value={mfaCode} onChange={setMfaCode} />
        </VStack>
        <WorkflowButton
          isDisabled={mfaCode.length !== 6}
          isLoading={verify.isLoading}
          loadingText={mode === "login" ? "Setting up your investment…" : "Verifying…"}
          onClick={onVerify}
          mb={5}>
          Submit Code
        </WorkflowButton>
      </WorkflowForm>
      {mode === "mfa" && (
        <HStack alignSelf="center">
          <Text textStyle="body2" m={0}>
            Didn't receive a code?
          </Text>
          <Text
            textStyle="body2"
            m={0}
            as="button"
            type="button"
            fontWeight="600"
            textDecoration={isActive ? "none" : "underline"}
            cursor={isActive ? "default" : "pointer"}
            onClick={onResend}>
            {isActive ? `Resend in ${countdown}s` : "Resend code"}
          </Text>
        </HStack>
      )}
    </OpenMfaContainer>
  );
};

export default WorkflowOpenMfa;
